/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// models
import Image from "models/image/Image.js";
import Model from "models/model/Model.js";

// utils
import ConversionUtils from "utils/conversion-utils/ConversionUtils.js";
import LoggerUtils from "utils/logger-utils/LoggerUtils.js";
import ValidationUtils from "utils/validation-utils/ValidationUtils.js";

// constants
const COLOR_DANGER_DEFAULT = String("#dc3545");
const COLOR_DARK_DEFAULT = String("#343a40");
const COLOR_INFO_DEFAULT = String("#17a2b8");
const COLOR_LIGHT_DEFAULT = String("#f8f9fa");
const COLOR_PRIMARY_DEFAULT = String("#007bff");
const COLOR_SECONDARY_DEFAULT = String("#6c757d");
const COLOR_SUCCESS_DEFAULT = String("#28a745");
const COLOR_WARNING_DEFAULT = String("#ffc107");
const MAP_LAT_DEFAULT = Number("0.0");
const MAP_LNG_DEFAULT = Number("0.0");
const MAP_ZOOM_DEFAULT = Number("10");
const RGB_SUM_WHITE = Number("765");
const TITLE_DEFAULT = String("Application-Title-Not-Set");

class Application extends Model {
  constructor({
    appleAppStoreUrl = String("https://www.apple.com/app-store"),
    bannerImage = new Image(),
    bootImage = new Image(),
    colors = {
      danger: String(COLOR_DANGER_DEFAULT),
      dark: String(COLOR_DARK_DEFAULT),
      info: String(COLOR_INFO_DEFAULT),
      light: String(COLOR_LIGHT_DEFAULT),
      primary: String(COLOR_PRIMARY_DEFAULT),
      secondary: String(COLOR_SECONDARY_DEFAULT),
      success: String(COLOR_SUCCESS_DEFAULT),
      warning: String(COLOR_WARNING_DEFAULT),
    },
    downloadImage = new Image(),
    externalLinks = [],
    googlePlayStoreUrl = String("https://play.google.com/store"),
    iconImage = new Image(),
    id = String("application"),
    lastUpdated = Model.getDefaultLastUpdated(),
    mapProperties = {
      position: {
        lat: Number(MAP_LAT_DEFAULT),
        lng: Number(MAP_LNG_DEFAULT),
      },
      zoom: Number(MAP_ZOOM_DEFAULT),
    },
    policyUrl = String("https://www.art.relyt.app/policy"),
    qrcodeImages = [],
    qrcodePrefix = String("not-set"),
    tags = [{ model: "Application" }],
    title = String(TITLE_DEFAULT),
  } = {}) {

    // state
    super({ id, lastUpdated, tags });
 
    this.state = {
      ...this.state,
      appleAppStoreUrl,
      bannerImage,
      bootImage,
      colors,
      downloadImage,
      externalLinks,
      googlePlayStoreUrl,
      iconImage,
      mapProperties,
      policyUrl,
      qrcodeImages,
      qrcodePrefix,
      title,
    };

    // setters
    this.setAppleAppStoreUrl(appleAppStoreUrl);
    this.setBannerImage(bannerImage);
    this.setBootImage(bootImage);
    this.setColorDanger(colors.danger);
    this.setColorDark(colors.dark);
    this.setColorInfo(colors.info);
    this.setColorLight(colors.light);
    this.setColorPrimary(colors.primary);
    this.setColorSecondary(colors.secondary);
    this.setColorSuccess(colors.success);
    this.setColorWarning(colors.warning);
    this.setDownloadImage(downloadImage);
    this.setExternalLinks(externalLinks);
    this.setGooglePlayStoreUrl(googlePlayStoreUrl);
    this.setIconImage(iconImage);
    this.setMapPosition(mapProperties.position);
    this.setMapZoom(mapProperties.zoom);
    this.setPolicyUrl(policyUrl);
    this.setQrCodeImages(qrcodeImages);
    this.setQrCodePrefix(qrcodePrefix);
    this.setTitle(title);
  }

  /*====================
  Getters
  ====================*/
  getAppleAppStoreUrl() { return String(this.state.appleAppStoreUrl); }
  getBannerImage() { return this.state.bannerImage; }
  getBootImage() { return this.state.bootImage; }
  getColorDanger() { return String(this.state.colors.danger); }
  getColorDangerCompliment() { return Application.getColorCompliment(this.getColorDanger()); }
  getColorDark() { return String(this.state.colors.dark); }
  getColorDarkCompliment() { return Application.getColorCompliment(this.getColorDark()); }
  getColorInfo() { return String(this.state.colors.info); }
  getColorInfoCompliment() { return Application.getColorCompliment(this.getColorInfo()); }
  getColorLight() { return String(this.state.colors.light); }
  getColorLightCompliment() { return Application.getColorCompliment(this.getColorLight()); }
  getColorPrimary() { return String(this.state.colors.primary); }
  getColorPrimaryCompliment() { return Application.getColorCompliment(this.getColorPrimary()); }
  getColorSecondary() { return String(this.state.colors.secondary); }
  getColorSecondaryCompliment() { return Application.getColorCompliment(this.getColorSecondary()); }
  getColorSuccess() { return String(this.state.colors.success); }
  getColorSuccessCompliment() { return Application.getColorCompliment(this.getColorSuccess()); }
  getColorWarning() { return String(this.state.colors.warning); }
  getColorWarningCompliment() { return Application.getColorCompliment(this.getColorWarning()); }
  getDownloadImage() { return this.state.downloadImage; }
  getExternalLinks() { return [ ...this.state.externalLinks ]; }
  getGooglePlayStoreUrl() { return String(this.state.googlePlayStoreUrl); }
  getIconImage() { return this.state.iconImage; }
  getMapPosition() {
    return {
      lat: Number(this.state.mapProperties.position.lat),
      lng: Number(this.state.mapProperties.position.lng),
    };
  }
  getMapZoom() { return Number(this.state.mapProperties.zoom); }
  getPolicyUrl() { return String(this.state.policyUrl); }
  getQrCodeImages() { return this.state.qrcodeImages; }
  getQrCodePrefix() { return String(this.state.qrcodePrefix); }
  getTitle() { return String(this.state.title); }

  getState() {
    return {
      ...this.getModelState(),
      appleAppStoreUrl: this.getAppleAppStoreUrl(),
      bannerImage: this.getBannerImage().getState(),
      bootImage: this.getBootImage().getState(),
      colors: {
        danger: this.getColorDanger(),
        dark: this.getColorDark(),
        info: this.getColorInfo(),
        light: this.getColorLight(),
        primary: this.getColorPrimary(),
        secondary: this.getColorSecondary(),
        success: this.getColorSuccess(),
        warning: this.getColorWarning()
      },
      downloadImage: this.getDownloadImage().getState(),
      externalLinks: this.getExternalLinks(),
      googlePlayStoreUrl: this.getGooglePlayStoreUrl(),
      iconImage: this.getIconImage().getState(),
      mapProperties: {
        position: this.getMapPosition(),
        zoom: this.getMapZoom(),
      },
      policyUrl: this.getPolicyUrl(),
      qrcodeImages: this.getQrCodeImages().map((image) => image.getState()),
      qrcodePrefix: this.getQrCodePrefix(),
      title: this.getTitle(),
    };
  }

  /*====================
  Setters
  ====================*/
  /**
  * @param {String} appleAppStoreUrl  (url)
  * @returns {String} appleAppStoreUrl  (url)
  */
  setAppleAppStoreUrl(appleAppStoreUrl) {
    if(ValidationUtils.url(appleAppStoreUrl)) {
      this.state.appleAppStoreUrl = String(appleAppStoreUrl);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (url) setAppleAppStoreUrl.",
        appleAppStoreUrl,
      );
    }
    return this.getAppleAppStoreUrl();
  }

  /**
  * @param {Image | Object} image
  * @returns {Image} image
  */
  setBannerImage(image) {
    if(image instanceof Image) {
      this.state.bannerImage = image;
    }
    else {
      this.state.bannerImage = new Image(image);
    }
    this.onChange();
    return this.getBannerImage();
  }

  /**
  * @param {Image | Object} image
  * @returns {Image} image
  */
  setBootImage(image) {
    if(image instanceof Image) {
      this.state.bootImage = image;
    }
    else {
      this.state.bootImage = new Image(image);
    }
    this.onChange();
    return this.getBootImage();
  }

  /**
  * @param {String} color (colorHex)
  * @returns {String} color (colorHex)
  */
  setColorDanger(color) {
    if (ValidationUtils.colorHex(color)) {
      this.state.colors.danger = String(color);
      this.state.colors.dangerCompliment = Application.getColorCompliment(color);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (colorHex) setColorDanger.",
        color,
      );
    }
    return this.getColorDanger();
  }

  /**
  * @param {String} color (colorHex)
  * @returns {String} color (colorHex)
  */
  setColorDark(color) {
    if (ValidationUtils.colorHex(color)) {
      this.state.colors.dark = String(color);
      this.state.colors.darkCompliment = Application.getColorCompliment(color);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (colorHex) setColorDark.",
        color,
      );
    }
    return this.getColorDark();
  }

  /**
  * @param {String} color (colorHex)
  * @returns {String} color (colorHex)
  */
  setColorInfo(color) {
    if (ValidationUtils.colorHex(color)) {
      this.state.colors.info = String(color);
      this.state.colors.infoCompliment = Application.getColorCompliment(color);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (colorHex) setColorInfo.",
        color,
      );
    }
    return this.getColorInfo();
  }

  /**
  * @param {String} color (colorHex)
  * @returns {String} color (colorHex)
  */
  setColorLight(color) {
    if (ValidationUtils.colorHex(color)) {
      this.state.colors.light = String(color);
      this.state.colors.lightCompliment = Application.getColorCompliment(color);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (colorHex) setColorLight.",
        color,
      );
    }
    return this.getColorLight();
  }

  /**
  * @param {String} color (colorHex)
  * @returns {String} color (colorHex)
  */
  setColorPrimary(color) {
    if (ValidationUtils.colorHex(color)) {
      this.state.colors.primary = String(color);
      this.state.colors.primaryCompliment = Application.getColorCompliment(color);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (colorHex) setColorPrimary.",
        color,
      );
    }
    return this.getColorPrimary();
  }

  /**
  * @param {String} color (colorHex)
  * @returns {String} color (colorHex)
  */
  setColorSecondary(color) {
    if (ValidationUtils.colorHex(color)) {
      this.state.colors.secondary = String(color);
      this.state.colors.secondaryCompliment = Application.getColorCompliment(color);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (colorHex) setColorSecondary.",
        color,
      );
    }
    return this.getColorSecondary();
  }

  /**
  * @param {String} color (colorHex)
  * @returns {String} color (colorHex)
  */
  setColorSuccess(color) {
    if (ValidationUtils.colorHex(color)) {
      this.state.colors.success = String(color);
      this.state.colors.successCompliment = Application.getColorCompliment(color);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (colorHex) setColorSuccess.",
        color,
      );
    }
    return this.getColorSuccess();
  }

  /**
  * @param {String} color (colorHex)
  * @returns {String} color (colorHex)
  */
  setColorWarning(color) {
    if (ValidationUtils.colorHex(color)) {
      this.state.colors.warning = String(color);
      this.state.colors.warningCompliment = Application.getColorCompliment(color);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (colorHex) setColorWarning.",
        color,
      );
    }
    return this.getColorWarning();
  }

  /**
  * @param {Image | Object} image
  * @returns {Image} image
  */
  setDownloadImage(image) {
    if(image instanceof Image) {
      this.state.downloadImage = image;
    }
    else {
      this.state.downloadImage = new Image(image);
    }
    this.onChange()
    return this.getDownloadImage();
  }

  /**
  * @param {Object[]} linksArray ([{ label: String, url: String }])
  * @returns {Object[]} linksArray ([{ label: String, url: String }])
  */
  setExternalLinks(linksArray) {
    let validationPassed = true;
    if (ValidationUtils.array(linksArray)) {
      const externalLinks = [];
      for (const index in linksArray) {
        if (
          linksArray[index] &&
          linksArray[index].hasOwnProperty("label") &&
          linksArray[index].hasOwnProperty("url") &&
          ValidationUtils.string(linksArray[index].label) &&
          ValidationUtils.string(linksArray[index].url)
        ) {
          externalLinks[index] = {
            label: String(linksArray[index].label),
            url: String(linksArray[index].url),
          };
        }
        else {
          validationPassed = false;
          break;
        }
      }
      this.state.externalLinks = externalLinks;
      this.onChange();
    }
    else {
      validationPassed = false;
    }

    if(!validationPassed) {
      LoggerUtils.error(
        "Unable to validate (array, { string, string }) setExternalLinks.",
        linksArray,
      );
    }
    return this.getExternalLinks();
  }

  /**
  * @param {String} googlePlayStoreUrl (url)
  * @returns {String} googlePlayStoreUrl (url)
  */
  setGooglePlayStoreUrl(googlePlayStoreUrl) {
    if(ValidationUtils.url(googlePlayStoreUrl)) {
      this.state.googlePlayStoreUrl = String(googlePlayStoreUrl);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (url) setGooglePlayStoreUrl.",
        googlePlayStoreUrl,
      );
    }
    return this.getGooglePlayStoreUrl();
  }

  /**
  * @param {Image | Object} image
  * @returns {Image} image
  */
  setIconImage(image) {
    if(image instanceof Image) {
      this.state.iconImage = image;
    }
    else {
      this.state.iconImage = new Image(image);
    }
    this.onChange();
    return this.getIconImage();
  }

  /**
  * @param {Object} position ({ lat: Float, lng: Float })
  * @returns {Object} position ({ lat: Float, lng: Float })
  */
  setMapPosition(position) {
    if (ValidationUtils.latLng(position)) {
      this.state.mapProperties.position = {
        lat: Number(position.lat),
        lng: Number(position.lng),
      };
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (latLng) setMapPosition.",
        position,
      );
    }
    return this.getMapPosition();
  }

  /**
  * @param {Number} zoom (Float)
  * @returns {Number} zoom (Float)
  */
  setMapZoom(zoom) {
    if (ValidationUtils.float(zoom)) {
      this.state.mapProperties.zoom = Number(zoom);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (float) setMapZoom.",
        zoom,
      );
    }
    return this.getMapZoom();
  }

  /**
  * @param {String} policyUrl (url)
  * @returns {String} policyUrl (url)
  */
  setPolicyUrl(policyUrl) {
    if(ValidationUtils.url(policyUrl)) {
      this.state.policyUrl = String(policyUrl);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (url) setPolicyUrl.",
        policyUrl,
      );
    }
    return this.getPolicyUrl();
  }

  /**
  * @param {Image[] | Object[]} images
  * @returns {Image[]}
  */
  setQrCodeImages(images) {
    if(ValidationUtils.array(images)) {

      const qrcodeImages = [];
      for(const index in images) {
        if(images[index]) {
          const image = images[index];
          if(image instanceof Image) {
            qrcodeImages.push(image);
          }
          else {
            qrcodeImages.push(new Image(image));
          }
        }
      }

      this.state.qrcodeImages = qrcodeImages;
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (array) setQrCodeImages.",
        images,
      );
    }
    return this.getQrCodeImages();
  }

  /**
  * @param {String} qrcodePrefix
  * @returns {String} qrcodePrefix
  */
  setQrCodePrefix(qrcodePrefix) {
    if(ValidationUtils.string(qrcodePrefix)) {
      this.state.qrcodePrefix = String(qrcodePrefix);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (string) setQrCodePrefix.",
        qrcodePrefix,
      );
    }
    return this.getQrCodePrefix();
  }

  /**
  * @param {String} title
  * @returns {String} title
  */
  setTitle(title) {
    if(ValidationUtils.string(title)) {
      this.state.title = String(title);
      this.onChange();
    }
    else if(String(title).trim() === String("")) {
      this.state.title = String(TITLE_DEFAULT);
      this.onChange();
    }
    else {
      LoggerUtils.error(
        "Unable to validate (string) setTitle.",
        title,
      );
    }
    return this.getTitle();
  }

  /*====================
  helpers
  ====================*/
  /**
  * @param {String} label
  * @param {String} url
  * @returns {Object[]} linksArray ([{ label: String, url: String }])
  */
  addExternalLink(label, url) {
    if(ValidationUtils.string(label) && ValidationUtils.url(url)) {
      const externalLinks = this.getExternalLinks();
      externalLinks.push({
        label: String(label),
        url: String(url),
      });
      this.setExternalLinks(externalLinks);
    }
    else {
      LoggerUtils.error(
        "Unable to validate (label, url) addExternalLink.",
        `${label}, ${url}`
      );
    }
    return this.getExternalLinks();
  }

  /**
  * @param {Image | Object} image
  * @returns {Image[]}
  */
  addQrCodeImage(image) {
    if(image instanceof Image) {
      const images = this.getQrCodeImages();
      images.push(image);
      this.setQrCodeImages(images);
    }
    else {
      const images = this.getQrCodeImages();
      if(ValidationUtils.exists(image.sources)) {
        images.push(new Image(image));
      }
      else {
        images.push(new Image({ sources: { full: image }}));
      }
      this.setQrCodeImages(images);
    }
    return this.getQrCodeImages();
  }

  /**
  * @param {String} color (colorHex)
  * @returns {String} color (colorHex)
  */
  static getColorCompliment(colorHex) {
    if (ValidationUtils.colorHex(colorHex)) {
      const colorRgb = ConversionUtils.colorHexToRgb(colorHex);
      const colorRgbSum = colorRgb[0] + colorRgb[1] + colorRgb[2]; // NOSONAR
      const distanceToMaxRgb = RGB_SUM_WHITE - colorRgbSum;
      const distanceToMinRgb = colorRgbSum; // x - 0 = x

      // return black if color is closer to white
      // return white if color is closer to black
      return distanceToMaxRgb < distanceToMinRgb ? "#000000" : "#FFFFFF";
    }
    // return white if passed an invalid color
    else {
      LoggerUtils.error(
        "Unable to validate (colorHex) getColorCompliment.",
        colorHex,
      );
      return "#FFFFFF";
    }
  }

  /**
  * @param {Number} externalLinkIndex
  * @returns {Object[]} linksArray ([{ label: String, url: String }])
  */
  removeExternalLink(externalLinkIndex) {
    if(ValidationUtils.integer(externalLinkIndex)) {
      const externalLinks = this.getExternalLinks();
      externalLinks.splice(externalLinkIndex, 1);
      return this.setExternalLinks(externalLinks);
    }
    else {
      LoggerUtils.error(
        "Unable to validate (integer) removeExternalLink.",
        externalLinkIndex,
      );
      return this.getExternalLinks();
    }
  }

  /**
  * @param {Number} imageIndex
  * @returns {Image[]}
  */
  removeQrCodeImage(imageIndex) {
    if(ValidationUtils.integer(imageIndex)) {
      const images = this.getQrCodeImages();
      if(imageIndex >= 0 && imageIndex < images.length) {
        images.splice(imageIndex, 1);
        this.setQrCodeImages(images);
      }
    }
    else {
      LoggerUtils.error(
        "Unable to validate (integer) removeQrCodeImage.",
        imageIndex,
      );
    }
    return this.getQrCodeImages();
  }

  /**
  * @param {Number} imageIndex
  * @returns {Image[]}
  */
  shiftQrCodeImageBackward(imageIndex) {
    if(ValidationUtils.integer(imageIndex)) {
      const images = this.getQrCodeImages();
      if(imageIndex < images.length - 1) {
        const index = Number(imageIndex);
        const indexPlusOne = Number(index + 1);
        const imageAtIndex = images[index];
        const imageAtIndexPlusOne = images[indexPlusOne];
        images[index] = imageAtIndexPlusOne;
        images[indexPlusOne] = imageAtIndex;
        this.setQrCodeImages(images);
      }
    }
    else {
      LoggerUtils.error(
        "Unable to validate (integer) shiftQrCodeImageBackward.",
        imageIndex,
      );
    }
    return this.getQrCodeImages();
  }

  /**
  * @param {Number} imageIndex
  * @returns {Image[]}
  */
  shiftQrCodeImageForward(imageIndex) {
    if(ValidationUtils.integer(imageIndex) && imageIndex > 0) {
      const images = this.getQrCodeImages();
      const index = Number(imageIndex);
      const indexMinusOne = Number(index - 1);
      const imageAtIndex = images[index];
      const imageAtIndexMinusOne = images[indexMinusOne];
      images[index] = imageAtIndexMinusOne;
      images[indexMinusOne] = imageAtIndex;
      this.setQrCodeImages(images);
    }
    else {
      LoggerUtils.error(
        "Unable to validate (integer) shiftQrCodeImageForward.",
        imageIndex,
      );
    }
    return this.getQrCodeImages();
  }

  /*====================
  validation
  ====================*/
  validate() { // NOSONAR
    const state = this.getState();

    // property checks
    // ====================
    if(!state.hasOwnProperty("appleAppStoreUrl"))
    { throw Error(`Missing property "appleAppStoreUrl".`); }

    else if(!state.hasOwnProperty("bannerImage"))
    { throw Error(`Missing property "bannerImage".`); }

    else if(!state.hasOwnProperty("bootImage"))
    { throw Error(`Missing property "bootImage".`); }

    else if(!state.hasOwnProperty("colors"))
    { throw Error(`Missing property "colors".`); }

    else if(!state.hasOwnProperty("downloadImage"))
    { throw Error(`Missing property "downloadImage".`); }

    else if(!state.hasOwnProperty("externalLinks"))
    { throw Error(`Missing property "externalLinks".`); }

    else if(!state.hasOwnProperty("googlePlayStoreUrl"))
    { throw Error(`Missing property "googlePlayStoreUrl".`); }

    else if(!state.hasOwnProperty("iconImage"))
    { throw Error(`Missing property "iconImage".`); }

    else if(!state.hasOwnProperty("lastUpdated"))
    { throw Error(`Missing property "lastUpdated".`); }

    else if(!state.hasOwnProperty("mapProperties"))
    { throw Error(`Missing property "mapProperties".`); }

    else if(!state.hasOwnProperty("policyUrl"))
    { throw Error(`Missing property "policyUrl".`); }

    else if(!state.hasOwnProperty("qrcodePrefix"))
    { throw Error(`Missing property "qrcodePrefix".`); }

    else if(!state.hasOwnProperty("qrcodeImages"))
    { throw Error(`Missing property "qrcodeImages".`); }

    else if(!state.hasOwnProperty("tags"))
    { throw Error(`Missing property "tags".`); }

    else if(!state.hasOwnProperty("title"))
    { throw Error(`Missing property "title".`); }

    // value checks
    // ====================
    const appleAppStoreUrl = this.getAppleAppStoreUrl();
    const bannerImage = this.getBannerImage();
    const bootImage = this.getBootImage();
    const colorDanger = this.getColorDanger();
    const colorDark = this.getColorDark();
    const colorInfo = this.getColorInfo();
    const colorLight = this.getColorLight();
    const colorPrimary = this.getColorPrimary();
    const colorSecondary = this.getColorSecondary();
    const colorSuccess = this.getColorSuccess();
    const colorWarning = this.getColorWarning();
    const downloadImage = this.getDownloadImage();
    const externalLinks = this.getExternalLinks();
    const googlePlayStoreUrl = this.getGooglePlayStoreUrl();
    const iconImage = this.getIconImage();
    const mapPropertiesPosition = this.getMapPosition();
    const mapPropertiesZoom = this.getMapZoom();
    const lastUpdated = this.getLastUpdated();
    const policyUrl = this.getPolicyUrl();
    const qrcodePrefix = this.getQrCodePrefix();
    const qrcodeImages = this.getQrCodeImages();
    const tags = this.getTags();
    const title = this.getTitle();

    if(!ValidationUtils.url(appleAppStoreUrl)) {
      throw Error(
        `Invalid property "appleAppStoreUrl" does not pass validation "url": ` +
        `${JSON.stringify(appleAppStoreUrl)}`
      );
    }

    else if(!ValidationUtils.exists(bannerImage)) {
      throw Error(
        `Invalid property "bannerImage" does not pass validation "exists": ` +
        `${JSON.stringify(bannerImage)}`
      );
    }

    else if(!ValidationUtils.exists(bootImage)) {
      throw Error(
        `Invalid property "bootImage" does not pass validation "exists": ` +
        `${JSON.stringify(bootImage)}`
      );
    }

    else if(!ValidationUtils.colorHex(colorDanger)) {
      throw Error(
        `Invalid property "colorDanger" does not pass validation "colorHex": ` +
        `${JSON.stringify(colorDanger)}`
      );
    }

    else if(!ValidationUtils.colorHex(colorDark)) {
      throw Error(
        `Invalid property "colorDark" does not pass validation "colorHex": ` +
        `${JSON.stringify(colorDark)}`
      );
    }

    else if(!ValidationUtils.colorHex(colorInfo)) {
      throw Error(
        `Invalid property "colorInfo" does not pass validation "colorHex": ` +
        `${JSON.stringify(colorInfo)}`
      );
    }

    else if(!ValidationUtils.colorHex(colorLight)) {
      throw Error(
        `Invalid property "colorLight" does not pass validation "colorHex": ` +
        `${JSON.stringify(colorLight)}`
      );
    }

    else if(!ValidationUtils.colorHex(colorPrimary)) {
      throw Error(
        `Invalid property "colorPrimary" does not pass validation "colorHex": ` +
        `${JSON.stringify(colorPrimary)}`
      );
    }

    else if(!ValidationUtils.colorHex(colorSecondary)) {
      throw Error(
        `Invalid property "colorSecondary" does not pass validation "colorHex": ` +
        `${JSON.stringify(colorSecondary)}`
      );
    }

    else if(!ValidationUtils.colorHex(colorSuccess)) {
      throw Error(
        `Invalid property "colorSuccess" does not pass validation "colorHex": ` +
        `${JSON.stringify(colorSuccess)}`
      );
    }

    else if(!ValidationUtils.colorHex(colorWarning)) {
      throw Error(
        `Invalid property "colorWarning" does not pass validation "colorHex": ` +
        `${JSON.stringify(colorWarning)}`
      );
    }

    else if(!ValidationUtils.exists(downloadImage)) {
      throw Error(
        `Invalid property "downloadImage" does not pass validation "exists": ` +
        `${JSON.stringify(downloadImage)}`
      );
    }

    else if(!ValidationUtils.array(externalLinks)) {
      throw Error(
        `Invalid property "externalLinks" does not pass validation "array": ` +
        `${JSON.stringify(externalLinks)}`
      );
    }

    else if(!ValidationUtils.url(googlePlayStoreUrl)) {
      throw Error(
        `Invalid property "googlePlayStoreUrl" does not pass validation "url": ` +
        `${JSON.stringify(googlePlayStoreUrl)}`
      );
    }

    else if(!ValidationUtils.exists(iconImage)) {
      throw Error(
        `Invalid property "iconImage" does not pass validation "exists": ` +
        `${JSON.stringify(iconImage)}`
      );
    }

    else if(!ValidationUtils.string(lastUpdated)) {
      throw Error(
        `Invalid property "lastUpdated" does not pass validation "string": ` +
        `${JSON.stringify(lastUpdated)}`
      );
    }

    else if(!ValidationUtils.latLng(mapPropertiesPosition)) {
      throw Error(
        `Invalid property "mapPropertiesPosition" does not pass validation "latLng": ` +
        `${JSON.stringify(mapPropertiesPosition)}`
      );
    }

    else if(!ValidationUtils.float(mapPropertiesZoom)) {
      throw Error(
        `Invalid property "mapPropertiesZoom" does not pass validation "float": ` +
        `${JSON.stringify(mapPropertiesZoom)}`
      );
    }

    else if(!ValidationUtils.url(policyUrl)) {
      throw Error(
        `Invalid property "policyUrl" does not pass validation "url": ` +
        `${JSON.stringify(policyUrl)}`
      );
    }

    else if(!ValidationUtils.string(qrcodePrefix)) {
      throw Error(
        `Invalid property "qrcodePrefix" does not pass validation "string":` +
        `${JSON.stringify(qrcodePrefix)}`
      );
    }

    else if(!ValidationUtils.array(qrcodeImages)) {
      throw Error(
        `Invalid property "qrcodeImages" does not pass validation "array":` +
        `${JSON.stringify(qrcodeImages)}`
      );
    }

    else if(!ValidationUtils.array(tags)) {
      throw Error(
        `Invalid property "tags" does not pass validation "array":` +
        `${JSON.stringify(tags)}`
      );
    }

    else if(!ValidationUtils.string(title)) {
      throw Error(
        `Invalid property "title" does not pass validation "string":` +
        `${JSON.stringify(title)}`
      );
    }
  }
}
export default Application;
